import React, { useState, useContext, useMemo } from "react";
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Stack,
  useMediaQuery,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  List,
  ListItem,
  ListItemSecondaryAction,
  Alert,
  Avatar,
  Box,
  IconButton,
} from "@mui/material";
import {
  Add,
  Collections,
  Save,
  Link,
  History,
  Delete,
  FileCopyOutlined,
  InsertDriveFile,
  Folder,
  Info,
  Close,
} from "@mui/icons-material";
import MenuIcon from "@mui/icons-material/Menu";
import { useTheme } from "@mui/material/styles";
import { ProjectSchemeCreator } from "../projects_v2/projects_scheme/ProjectSchemeCreator";
import { useLoginState } from "../studioxr/auth/LoginState";
import { copyBoardFromId, deleteBoard, associateBoardWithProject } from "../v2/apis/boardsSchemeApi";
import { BoardSchemeInterface, CardTypes, ProfileInterface, ProjectSchemeInterface, UserProfileRole } from "ts-interfaces";
import {
  useMattoState,
  useProjectData,
  usePhysicalObjects,
} from "../MattoState";
import { useNavigate } from "react-router-dom";
import { AssociatedProjectModal } from "../projects_v2/components/AssociatedProjectModal";
import { DeleteComponentModal } from "../projects_v2/components/DeleteComponentModal";
import dayjs from "dayjs";
import { getVersionHistory, getVersionById } from "../libs/firebase";
import { processProjectData } from "../libs/projectUtils";
import { AuthContext } from "../auth/Auth";
import { getUrlFromRef } from "../libs/firebase";
import { cloudflareCDN } from "../libs/util";
import { firebaseHelper, usePermissions } from "ui";
import { ProIconButton } from "../buttons/ProIconButton";
import { useProjectSchemas } from "../global-state/useProjectSchemas";
import { useBoards } from "../global-state/useBoards";
import { SelectCollectionForDefaultModal } from "../components/SelectCollectionForDefaultModal";

interface FirestoreTimestamp {
  seconds: number;
  nanoseconds: number;
}

interface Version {
  versionId: string;
  versionTimestamp: FirestoreTimestamp | Date | string;
  files?: {
    screenshot?: string;
  };
}

export const FileMenu = ({ currentUser, createNewBoard }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const [createProjectScheme, setCreateProjectScheme] = useState(false);
  const [showAssociatedProjectModal, setShowAssociatedProjectModal] = useState(false);
  const [showDeleteBoardModal, setShowDeleteBoardModal] = useState(false);
	const isStateDirty = useMattoState((state) => state.isStateDirty)
  const setSnackbarMessage = useMattoState((state) => state.setSnackbarMessage);
  const getPermissions = useLoginState((state) => state.getPermmissions);
  const projectData: any = useProjectData();
  const history = useNavigate();
  const { permissions } = usePermissions(currentUser?.uid, firebaseHelper.db);
  const profile: ProfileInterface | null = useLoginState((state) => state.profile);
  const addProject = useProjectSchemas((state) => state.addProject);
  const invalidateCache = useProjectSchemas((state) => state.invalidateCache);

  // Enhance projectData with id field to match BoardSchemeInterface
  // This is necessary to ensure compatibility with the AssociatedProjectModal
  const enhancedProjectData: any = useMemo(() => {
    if (!projectData) return null;
    return {
      ...projectData,
      id: projectData.projectId,
    };
  }, [projectData]);

  // Version history states
  const [openSnack, setOpenSnack] = useState(false);
  const [snackSeverity, setSnackSeverity] = useState<
    "success" | "error" | "info" | "warning"
  >("info");
  const [message, setMessage] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(false);
  const [restoringVersion, setRestoringVersion] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [versionToRestore, setVersionToRestore] = useState<Version | null>(
    null
  );
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [showSelectCollectionModal, setShowSelectCollectionModal] = useState(false);

  const setPhysicalObjects = usePhysicalObjects(
    (state) => state.setPhysicalObjects
  );
  const updateProjectData = useProjectData((state) => state.update);
  const { currentUser: authUser } = useContext(AuthContext);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCopyBoard = async (boardId: string, userId: string) => {
    try {
      if (isStateDirty==true) {
        const x = window.confirm("Board unsaved! Are you sure you want to make a copy of the unsaved board? This will make you lose any unsaved changes.");
        if (x!=true) return;
      }
      setSnackbarMessage("Copying board...");
      const copiedBoard: BoardSchemeInterface = await copyBoardFromId(
        boardId,
        userId,
        await getPermissions()
      );
      setSnackbarMessage("Board copied successfully");
      console.log("Board copied successfully:", copiedBoard);
      history("/projects");
    } catch (error) {
      console.error("Error copying board:", error);
    }
  };

  const handleDeleteBoard = async () => {
    try {
      // Check if user is authenticated before proceeding
      if (!currentUser?.uid) {
        console.error("User not authenticated");
        setSnackbarMessage("Please log in to delete boards");
        return;
      }
      
      if (!projectData.projectId) return;
      
      await deleteBoard(
        projectData.projectId,
        currentUser.uid,
        await getPermissions()
      );
      history("/projects");
    } finally {
      setShowDeleteBoardModal(false);
    }
  };

  const handleMakeDefault = () => {
    setShowSelectCollectionModal(true);
  };

  const handleSelectCollectionForDefault = async (collectionId: string) => {
    try {
      setSnackbarMessage("Board set as default for the collection!");
    } catch (error) {
      console.error("Error setting board as default:", error);
      setSnackbarMessage("Failed to set board as default for collection");
    }
  };

  const handleAssociatedProjectChange = async ({ newProjectId, oldProjectId }) => {
    // Check if user is authenticated before proceeding
    if (!currentUser?.uid) {
      console.error("User not authenticated");
      setSnackbarMessage("Please log in to associate projects");
      return;
    }

    const enhancedProjectDataWithScreenshot = {
      ...enhancedProjectData,
      files: { screenshot: enhancedProjectData.screenshot || "" },
    };

    try {
        await associateBoardWithProject(enhancedProjectData.id, newProjectId, currentUser.uid, permissions);
        
        const updatedBoard = {
            ...enhancedProjectDataWithScreenshot,
            associatedProjectId: newProjectId,
            updatedAt: Date.now()
        };
        
        // If moving from one project to another
        if (oldProjectId && oldProjectId !== newProjectId) {
            useProjectSchemas.getState().removeBoardFromProjects(enhancedProjectDataWithScreenshot.id);
        }
        
        // Add to new project
        if (newProjectId) {
            useProjectSchemas.getState().updateBoardInProjects(updatedBoard);
        }
        
        console.log('updatedBoard', updatedBoard);

        // Update board store
        useBoards.getState().setBoards(
            useBoards.getState().boards.map(b => b.id === enhancedProjectData.id ? updatedBoard : b)
        );
        setShowAssociatedProjectModal(false);
        setSnackbarMessage('Board association updated successfully');
    } catch (error) {
        console.error('Error updating board association:', error);
        setSnackbarMessage(error.message || 'Failed to update board association');
    }
  };

  // Version history functions
  const showSnackbar = (
    message: string,
    severity: "success" | "error" | "info" | "warning" = "info"
  ) => {
    setMessage(message);
    setSnackSeverity(severity);
    setOpenSnack(true);
  };

  const handleVersionHistory = async () => {
    if (!authUser?.uid) {
      showSnackbar("Please log in to view version history", "warning");
      return;
    }

    if (!projectData.projectId) {
      showSnackbar(
        "Please save the project first to view version history",
        "warning"
      );
      return;
    }

    setLoading(true);
    setOpenDialog(true);

    try {
      // Call getVersionHistory function
      const versionHistory = await getVersionHistory(
        "projects",
        projectData.projectId
      );
      setVersions(versionHistory);
    } catch (error) {
      console.error("Error fetching version history:", error);
      showSnackbar("Error loading version history", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmRestore = (version: Version) => {
    setVersionToRestore(version);
    setConfirmDialogOpen(true);
  };

  const restoreVersion = async () => {
    if (!versionToRestore) return;

    setRestoringVersion(true);
    setConfirmDialogOpen(false);

    try {
      // Get the full version data
      const versionData = await getVersionById(
        "projects",
        projectData.projectId,
        versionToRestore.versionId
      );

      // Process the data using our shared utility function
      const processedData = processProjectData(versionData);

      // Update the physical objects state
      setPhysicalObjects(processedData.objects);

      // Update project data
      updateProjectData({
        ...versionData.projectData,
        ...{
          lighting: processedData.lighting,
          backgroundTexture: processedData.backgroundTexture,
          cameraPosition: processedData.cameraPosition,
          controlsTarget: processedData.controlsTarget,
          screenshot: processedData.screenshot,
        },
      });

      setOpenDialog(false);
      showSnackbar("Version successfully restored", "success");
    } catch (error) {
      console.error("Error restoring version:", error);
      showSnackbar("Error restoring version", "error");
    } finally {
      setRestoringVersion(false);
      setVersionToRestore(null);
    }
  };

  const formatDate = (
    timestamp: FirestoreTimestamp | Date | string
  ): string => {
    if (!timestamp) return "Unknown date";

    if (
      timestamp &&
      typeof timestamp === "object" &&
      "seconds" in timestamp &&
      "nanoseconds" in timestamp
    ) {
      // Convert Firestore Timestamp to JavaScript Date
      const firestoreTimestamp = timestamp as FirestoreTimestamp;
      const date = new Date(
        firestoreTimestamp.seconds * 1000 +
          firestoreTimestamp.nanoseconds / 1000000
      );
      return dayjs(date).format("MMM D YYYY [at] h:mm A");
    }

    if (timestamp instanceof Date) {
      return dayjs(timestamp).format("MMM D YYYY [at] h:mm A");
    }

    try {
      if (typeof timestamp === "string" && timestamp.includes("UTC")) {
        const date = new Date(timestamp);
        if (!isNaN(date.getTime())) {
          return dayjs(date).format("MMM D YYYY [at] h:mm A");
        }
      }

      if (typeof timestamp === "string") {
        return dayjs(timestamp).format("MMM D YYYY [at] h:mm A");
      }

      return "Invalid date format";
    } catch (error) {
      console.error("Error parsing date:", error, timestamp);
      return "Invalid date";
    }
  };

  const handleMenuItemClick = (action) => {
    console.log(`Action selected: ${action}`);
    let message = "";
    // Implement your action handlers here
    switch (action) {
      case "create-board":
        createNewBoard();
        break;
      case "create-collection":
        useMattoState.getState().setModal({
          name: "collection",
          id: null,
          associatedProjectsList: [],
        });
        break;
      case "create-project":
        if (isStateDirty==true) {
          const x = window.confirm("Board unsaved! This action will make you lose any unsaved changes.");
          if (x!=true) return;
        }
        setCreateProjectScheme(true);
        break;
      case "save-board":
        useMattoState.setState({ saveProjectNow: true });
        break;
      case "copy-board":
        if (!currentUser?.uid) {
          setSnackbarMessage("Please log in to copy boards");
          break;
        }
        if (projectData.projectId) {
          handleCopyBoard(projectData.projectId, currentUser.uid);
        } else {
          alert("Please save your board before making a copy.");
        }
        break;
      case "associate-project":
        setShowAssociatedProjectModal(true);
        break;
      case "make-default":
        if (projectData.projectId) {
          handleMakeDefault();
        } else {
          alert("Please save your board before making it default.");
        }
        break;
      case "version-history":
        handleVersionHistory();
        break;
      case "delete-board":
        setShowDeleteBoardModal(true);
        break;
      default:
        break;
    }
    handleClose();
  };

  const handleProjectCreated = (newProject: ProjectSchemeInterface) => {
    if (newProject) {
      addProject(newProject); // Add to store immediately
    }
    history("/projects/projects");
  };

  return (
    <>
      <Stack
        direction="row"
        sx={{
          paddingLeft: isXs ? 2 : 4,
          paddingRight: isXs ? 1 : 2
        }}
      >
        <Button
          id="file-button"
          aria-controls={open ? "file-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={handleClick}
          startIcon={<MenuIcon />}
          sx={{ textTransform: "none" }}
        >
          File
        </Button>
        <Menu
          id="file-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "file-button",
          }}
          slotProps={{
            paper: {
              sx: {
                "& .MuiListItemText-root": { whiteSpace: "normal" },
                "& .MuiListItemIcon-root": { color: "black" },
                width: '320px'
              },
            },
          }}
        >
          <MenuItem onClick={() => handleMenuItemClick("create-board")}>
            <ListItemIcon>
              <Add fontSize="small" />
            </ListItemIcon>
            <ListItemText>Create a New Board</ListItemText>
          </MenuItem>

          <MenuItem onClick={() => handleMenuItemClick("create-collection")}>
            <ListItemIcon>
              <Collections fontSize="small" />
            </ListItemIcon>
            <ListItemText>Create a New Collection</ListItemText>
          </MenuItem>

          <MenuItem onClick={() => handleMenuItemClick("create-project")} disabled={!(profile?.userRole === UserProfileRole.PRO)}>
            <ListItemIcon>
              <Folder fontSize="small" />
            </ListItemIcon>
            <ListItemText>Create a New Project</ListItemText>
            <ProIconButton id={enhancedProjectData?.id} size="32px" />
          </MenuItem>
          <Divider />

          <MenuItem onClick={() => handleMenuItemClick("save-board")}>
            <ListItemIcon>
              <Save fontSize="small" />
            </ListItemIcon>
            <ListItemText>Save this Board</ListItemText>
          </MenuItem>

          <MenuItem onClick={() => handleMenuItemClick("copy-board")}>
            <ListItemIcon>
              <FileCopyOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText>Make a Copy of this Board</ListItemText>
          </MenuItem>

          <Divider />

          <MenuItem onClick={() => handleMenuItemClick("associate-project")} disabled={!(profile?.userRole === UserProfileRole.PRO)}>
            <ListItemIcon>
              <Link fontSize="small" />
            </ListItemIcon>
            <ListItemText>Add / Change Associated Project</ListItemText>
            <ProIconButton id={enhancedProjectData?.id} size="32px" />
          </MenuItem>

          <MenuItem onClick={() => handleMenuItemClick("make-default")}>
            <ListItemIcon>
              <InsertDriveFile fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              Make this Board Default for a Collection
            </ListItemText>
          </MenuItem> 

          <Divider />

          {/* <MenuItem onClick={() => handleMenuItemClick("version-history")}>
            <ListItemIcon>
              <History fontSize="small" />
            </ListItemIcon>
            <ListItemText>Last 10 Key Revisions</ListItemText>
          </MenuItem> */}

          {/* <Divider /> */}

          <MenuItem onClick={() => handleMenuItemClick("delete-board")}>
            <ListItemIcon>
              <Delete fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ color: "error" }}>
              Delete this Board
            </ListItemText>
          </MenuItem>
        </Menu>
      </Stack>

      {createProjectScheme && (
        <ProjectSchemeCreator
          project={{}}
          setShowCreatorModal={setCreateProjectScheme}
          updateProject={handleProjectCreated}
        />
      )}
      {showAssociatedProjectModal && (
        <AssociatedProjectModal
          open={showAssociatedProjectModal}
          onClose={() => setShowAssociatedProjectModal(false)}
          onSave={handleAssociatedProjectChange}
          data={enhancedProjectData}
          type={CardTypes.Boards}
        />
      )}
      {showDeleteBoardModal && (
        <DeleteComponentModal
          open={showDeleteBoardModal}
          setOpen={setShowDeleteBoardModal}
          onDelete={handleDeleteBoard}
        />
      )}

      {showSelectCollectionModal && (
        <SelectCollectionForDefaultModal
          open={showSelectCollectionModal}
          onClose={() => setShowSelectCollectionModal(false)}
          onSelectCollection={handleSelectCollectionForDefault}
          currentBoardId={projectData.projectId || ''}
        />
      )}

      {/* Version History Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        aria-labelledby="version-history-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="version-history-dialog-title" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Last 10 Key Revisions</span>
          <IconButton 
            onClick={() => setInfoDialogOpen(true)}
            size="small"
          >
            <Info fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <p>Loading...</p>
          ) : versions.length === 0 ? (
            <p>No previous key revisions found</p>
          ) : (
            <List>
              {versions.map((version, index) => {
                const thumbnailUrl: string | undefined = version.files
                  ?.screenshot
                  ? cloudflareCDN(
                      getUrlFromRef(version.files.screenshot),
                      "height=120,format=auto"
                    )
                  : "none";

                return (
                  <ListItem
                    key={version.versionId}
                    divider={index < versions.length - 1}
                    sx={{ alignItems: "flex-start" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      {/* Thumbnail */}
                      <Avatar
                        variant="rounded"
                        sx={{
                          width: 80,
                          height: 60,
                          marginRight: 2,
                          border: "1px solid #e0e0e0",
                        }}
                        src={thumbnailUrl}
                      >
                        {!version.files?.screenshot && "📷"}
                      </Avatar>

                      {/* Version Info */}
                      <Box sx={{ flexGrow: 1 }}>
                        <ListItemText
                          primary={`Key Revision ${versions.length - index}`}
                          secondary={formatDate(version.versionTimestamp)}
                        />
                      </Box>

                      {/* Restore Button */}
                      <ListItemSecondaryAction>
                        <Button
                          onClick={() => handleConfirmRestore(version)}
                          disabled={restoringVersion}
                          size="small"
                          variant="outlined"
                        >
                          {restoringVersion ? "Restoring..." : "Restore"}
                        </Button>
                      </ListItemSecondaryAction>
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="confirm-restore-dialog-title"
        aria-describedby="confirm-restore-dialog-description"
      >
        <DialogTitle id="confirm-restore-dialog-title">
          Restore Key Revision
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-restore-dialog-description">
            Are you sure you want to restore this key revision? Current unsaved
            changes will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={restoreVersion} color="primary" variant="contained">
            Restore
          </Button>
        </DialogActions>
      </Dialog>

      {/* Info Dialog */}
      <Dialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        aria-labelledby="info-dialog-title"
        aria-describedby="info-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="info-dialog-title" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>About Key Revisions</span>
          <IconButton 
            onClick={() => setInfoDialogOpen(false)}
            size="small"
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="info-dialog-description">
          Save up to 10 key checkpoints. Rapid edits are grouped automatically, so you can easily roll back to the moments that matter.
          </DialogContentText>
        </DialogContent>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        open={openSnack}
        onClose={() => setOpenSnack(false)}
        autoHideDuration={5000}
      >
        <Alert onClose={() => setOpenSnack(false)} severity={snackSeverity}>
          {message}
        </Alert>
      </Snackbar>
    </>
  );
};
